// @ts-expect-error virtual file
import { addRouteMiddleware, defineNuxtPlugin, isNuxtError, navigateTo, useRequestURL } from '#imports'
import { isAfter, subMinutes } from 'date-fns'
import { useDashboardAppUrl } from '../composables/dashboard'
import { isSsoAppUrl, useSsoAppUrl } from '../composables/sso'
import { useUserSession } from '../composables/userSession'

export default defineNuxtPlugin(async (nuxt) => {
  const { accessToken, isReady: isUserReady, isLoggedIn, fetch: fetchUser, login, logout, refreshToken } = useUserSession()

  async function redirectToLogin() {
    const url = useRequestURL()

    if (isSsoAppUrl(url.origin)) {
      return await navigateTo({
        name: 'login',
      })
    }

    const newQuery = { ...Object.fromEntries(url.searchParams.entries()) }
    delete newQuery.accessToken

    const dashboardAppUrl = useDashboardAppUrl()({
      path: url.path,
      query: newQuery,
    })

    const ssoAppUrl = useSsoAppUrl()({
      path: 'login',
      query: {
        returnUrl: dashboardAppUrl,
      },
    })
    return await navigateTo({
      path: ssoAppUrl,
    }, {
      external: true,
    })
  }

  nuxt.hook('app:error', async (error) => {
    if (isNuxtError(error) && error.status === 401) {
      await redirectToLogin()
    }
  })

  addRouteMiddleware('_authenticate', async (to) => {
    if (!isUserReady.value) {
      await nuxt.runWithContext(() => fetchUser())
    }

    if (to.query.relogin && isLoggedIn.value) {
      await nuxt.runWithContext(() => logout())

      const newQuery = { ...to.query }
      delete newQuery.relogin

      return nuxt.runWithContext(() => {
        return navigateTo({
          path: to.path,
          query: newQuery,
        })
      })
    }

    if (to.query.accessToken) {
      await nuxt.runWithContext(() => login(to.query.accessToken))
      const newQuery = { ...to.query }
      delete newQuery.accessToken

      return nuxt.runWithContext(() => {
        return navigateTo({
          path: to.path,
          query: newQuery,
        })
      })
    }

    const currentAccessToken = accessToken.value

    if (
      (
        to.meta.authenticate
        && !isLoggedIn.value
      )
      || (
        to.meta.authenticate
        && currentAccessToken
        && isAfter(new Date(), currentAccessToken.expiresAt)
      )
    ) {
      return nuxt.runWithContext(() => redirectToLogin())
    }

    if (currentAccessToken) {
      if (isAfter(new Date(), subMinutes(currentAccessToken.expiresAt, 15))) {
        await nuxt.runWithContext(() => refreshToken())
      }
    }
  }, {
    global: true,
  })
})
